import { useNavigate } from "react-router";
import PaymentsLaunchSheet from "./PaymentsLaunchSheet";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Fixed when the dock sits on a patient page; absent on the home page,
     * where the create form asks who the payment is for.
     */
    subject?: { id: string; label: string } | null;
    /**
     * Links the new payment to the session it is charged for. Only the session
     * page has one; everywhere else the charge stands on its own.
     */
    sessionId?: string | null;
}

/**
 * Everything the Pagos dock entry opens.
 *
 * Lives here rather than in either dock so the two behave identically, and so
 * the docks themselves stay free of payments data. Unlike the scheduler's
 * equivalent it fetches nothing — both actions are navigations, and the pages
 * they land on already load what they need.
 */
export default function PaymentsDockActions({
    isOpen,
    onClose,
    subject = null,
    sessionId = null,
}: Props) {
    const navigate = useNavigate();

    // The create page reads `patientId` from the query string, which is the
    // same contract the session detail page uses for its "Register payment"
    // button. On the home page there is no patient in context, so it is left
    // off and the form asks.
    const newPaymentQuery = new URLSearchParams();
    if (subject) newPaymentQuery.set("patientId", subject.id);
    if (sessionId) newPaymentQuery.set("sessionId", sessionId);
    const newPaymentPath = newPaymentQuery.toString()
        ? `/payments/new?${newPaymentQuery}`
        : "/payments/new";

    // A patient page filters the list to that patient; the home page shows all.
    const listPath = subject
        ? `/payments?patientId=${encodeURIComponent(subject.id)}`
        : "/payments";

    return (
        <PaymentsLaunchSheet
            isOpen={isOpen}
            onClose={onClose}
            onNewPayment={() => navigate(newPaymentPath)}
            onShowPayments={() => navigate(listPath)}
            subjectLabel={subject?.label ?? null}
        />
    );
}
